/**
 * URL utilities - segurança e validação
 */

/**
 * Valida se uma URL é same-origin (segurança contra SSRF/exfiltração de credenciais).
 * Rejeita protocol-relative URLs (//evil.com) e off-origin URLs.
 *
 * @param url - URL a validar
 * @returns true se a URL é same-origin (relativa ou mesmo origin), false caso contrário
 */
export function isSameOriginUrl(url: string): boolean {
  if (!url) return false;

  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin === window.location.origin;
  } catch {
    // URL inválida
    return false;
  }
}

/**
 * Valida se uma URL de retorno é segura para redirecionamento (CVE-2026-22029).
 * Previne Open Redirect e XSS via `javascript:` — só aceita path interno relativo.
 *
 * @param url - URL a ser validada
 * @returns true se a URL é segura (path relativo interno)
 */
export function isValidReturnUrl(url: string | null | undefined): url is string {
  if (!url) return false;

  // Deve começar com / (path relativo)
  if (!url.startsWith('/')) return false;

  // Bloqueia redirect para a própria página de login (causa loop infinito)
  if (url.startsWith('/login')) return false;

  // Bloqueia URLs absolutas disfarçadas (//example.com)
  if (url.startsWith('//')) return false;

  // Navegadores tratam `\` como `/`: `/\evil.com` vira `//evil.com`
  if (url.includes('\\')) return false;

  // Bloqueia protocol handlers (javascript:, data:, vbscript:, etc.)
  if (url.includes(':')) return false;

  // Bloqueia caracteres de controle e encoding malicioso
  // eslint-disable-next-line no-control-regex -- intencionalmente detectando caracteres de controle
  if (/[\x00-\x1f\x7f]/.test(url)) return false;

  return true;
}

/**
 * Retorna uma URL de retorno segura, com fallback para URL padrão.
 *
 * @param url - URL candidata
 * @param fallback - URL padrão se a candidata for inválida
 * @returns URL segura para redirecionamento
 */
export function getSafeReturnUrl(url: string | null | undefined, fallback: string): string {
  return isValidReturnUrl(url) ? url : fallback;
}
